import '../main.js'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

GlobalWorkerOptions.workerSrc = workerUrl

const download = document.querySelector('#cv-download')
const status = document.querySelector('#cv-status')
const previewStatus = document.querySelector('#preview-status')
const viewer = document.querySelector('#cv-viewer')
const container = document.querySelector('#canvas-container')
const canvas = document.querySelector('#the-canvas')
const previous = document.querySelector('#prev')
const next = document.querySelector('#next')
let pdfDocument = null
let loadingTask = null
let renderTask = null
let pageNumber = 1
let renderId = 0
let rendering = false
let refreshing = false
let loadedVersion = null

function updateControls() {
    previous.disabled = rendering || !pdfDocument || pageNumber <= 1
    next.disabled = rendering || !pdfDocument || pageNumber >= pdfDocument.numPages
}

async function renderPage() {
    if (!pdfDocument) return
    const id = ++renderId
    const currentDocument = pdfDocument
    rendering = true
    updateControls()
    previewStatus.textContent = 'Loading preview…'
    try {
        if (renderTask) {
            renderTask.cancel()
            await renderTask.promise.catch(() => {})
        }
        const page = await currentDocument.getPage(pageNumber)
        if (id !== renderId) return
        const original = page.getViewport({ scale: 1 })
        const width = Math.max(container.clientWidth, 1)
        const viewport = page.getViewport({ scale: width / original.width })
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = Math.ceil(viewport.width * ratio)
        canvas.height = Math.ceil(viewport.height * ratio)
        canvas.setAttribute('aria-label', `CV page ${pageNumber} of ${currentDocument.numPages}`)
        document.querySelector('#page_num').textContent = pageNumber
        renderTask = page.render({
            canvasContext: canvas.getContext('2d'),
            viewport,
            transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0]
        })
        await renderTask.promise
        if (id === renderId) previewStatus.textContent = 'Download the PDF to open it in your preferred reader.'
    } catch (error) {
        if (id === renderId && error.name !== 'RenderingCancelledException') {
            previewStatus.textContent = 'The preview could not be displayed. Use Download CV to read the PDF.'
        }
    } finally {
        if (id === renderId) {
            renderTask = null
            rendering = false
            updateControls()
        }
    }
}

async function closeDocument() {
    ++renderId
    pdfDocument = null
    if (renderTask) {
        renderTask.cancel()
        await renderTask.promise.catch(() => {})
        renderTask = null
    }
    if (loadingTask) {
        await loadingTask.destroy()
        loadingTask = null
    }
    rendering = false
    viewer.hidden = true
    updateControls()
}

async function refresh() {
    if (refreshing) return
    refreshing = true
    try {
        const response = await fetch('/api/cv', { cache: 'no-store' })
        if (!response.ok) throw new Error('CV metadata unavailable')
        const cv = await response.json()
        download.hidden = !cv.available
        if (!cv.available) {
            await closeDocument()
            loadedVersion = null
            status.textContent = 'The CV will be available here soon.'
            previewStatus.textContent = ''
            return
        }
        status.textContent = `Updated ${new Date(cv.updatedAt).toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
        })}`
        const version = `${cv.updatedAt}:${cv.size}`
        if (version === loadedVersion) return
        await closeDocument()
        previewStatus.textContent = 'Loading preview…'
        try {
            loadingTask = getDocument({
                url: `/cv.pdf?updated=${encodeURIComponent(cv.updatedAt)}`,
                cMapUrl: '/assets/pdfjs/cmaps/',
                standardFontDataUrl: '/assets/pdfjs/standard_fonts/',
                wasmUrl: '/assets/pdfjs/wasm/',
                iccUrl: '/assets/pdfjs/iccs/'
            })
            pdfDocument = await loadingTask.promise
            pageNumber = 1
            document.querySelector('#page_count').textContent = pdfDocument.numPages
            viewer.hidden = false
            await renderPage()
            loadedVersion = version
        } catch {
            await closeDocument()
            loadedVersion = null
            previewStatus.textContent = 'The preview could not be displayed. Use Download CV to read the PDF.'
        }
    } catch {
        status.textContent = 'The CV is temporarily unavailable. Please try again shortly.'
    } finally {
        refreshing = false
    }
}

previous.addEventListener('click', () => {
    if (!pdfDocument || rendering || pageNumber <= 1) return
    pageNumber--
    renderPage()
})
next.addEventListener('click', () => {
    if (!pdfDocument || rendering || pageNumber >= pdfDocument.numPages) return
    pageNumber++
    renderPage()
})

let resizeTimer
window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => renderPage(), 150)
})
window.addEventListener('focus', refresh)
refresh()
